import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Skeleton, Button, notification } from 'src/common';
import { Paths } from 'src/pages/paths';
import styles from '../index.module.scss';
import AuthBack from '../AuthBack';
import { useLazyQuery } from '@apollo/client';
import { Auth } from 'aws-amplify';
import AuthWarning from '../AuthWarning';
import { GET_GROUP_ID } from 'src/models/auth/queries';
import { useApp, useAwsError, generateMsTeamsSsoUrl, UserType } from 'src/models/auth';
import { Translations } from 'src/lib/I18nService/types';
import { AmplifyError, AuthQueryParams } from 'src/models/auth/types';
import { useQueryParams } from 'src/helpers';
import { useNavigate } from 'react-router-dom';
import { StorageKey } from 'src/types/enums';

interface FormState {
  email: string;
}

export const SignInSSOPage: FC = () => {
  const { loading: isLoading, permission, error } = useApp();
  const { t } = useTranslation();
  const { t: cognito_tr } = useTranslation(Translations.cognitoErrors);
  const qp = useQueryParams();
  const navigate = useNavigate();
  const from = sessionStorage.getItem(StorageKey.FromLogin);
  const redirectTo = useMemo(
    () => (!!from ? from : permission === UserType.Free ? Paths.FREE_HOME_PATH : Paths.HOME_PATH),
    [from, permission]
  );
  const [form] = Form.useForm();
  const [awsError, clearAwsError] = useAwsError();

  const [warningVisible, setWarningVisible] = useState(false);
  const onOpenWarning = useCallback(() => {
    setWarningVisible(true);
  }, []);
  const onCloseWarning = useCallback(async () => {
    setWarningVisible(false);
    if (awsError) {
      clearAwsError();
    }
    if (error?.message === AmplifyError.DisabledUser) {
      await Auth.signOut();
    }
  }, [awsError, clearAwsError, error?.message]);

  const [cognitoError, setCognitoError] = useState<unknown | undefined>();
  const [ssoNotFound, setSsoNotFound] = useState(false);

  const [getGroupId, { loading: isSubmitting, error: loadSsoGroupError }] = useLazyQuery<
    { getSSOGroup: string | null },
    { email: string }
  >(GET_GROUP_ID, {
    fetchPolicy: 'no-cache',
    context: {
      requireNoAuth: true
    }
  });

  const initialValues = useMemo((): FormState => ({ email: '' }), []);

  useEffect(() => {
    if (cognitoError) {
      notification.error({
        message: t('Common.unknownError'),
        description: t('Common.unknownErrorDesc')
      });
    }
  }, [cognitoError, t]);

  useEffect(() => {
    if (error?.message === AmplifyError.DisabledUser || !!awsError || ssoNotFound) {
      onOpenWarning();
    }
  }, [awsError, error?.message, onOpenWarning, ssoNotFound]);

  useEffect(() => {
    if (loadSsoGroupError) {
      notification.error({
        message: t('Common.unknownError'),
        description: t('Common.unknownErrorDesc')
      });
    }
  }, [awsError, loadSsoGroupError, t]);

  const onFinish = useCallback(
    (values: FormState) => {
      const { email } = values;
      getGroupId({
        variables: {
          email
        }
      }).then(res => {
        const groupId = res.data?.getSSOGroup;
        if (qp[AuthQueryParams.MsState] && groupId !== null && groupId) {
          window.location.href = generateMsTeamsSsoUrl(groupId, qp[AuthQueryParams.MsState]);
        } else if (groupId !== null) {
          groupId &&
            Auth.federatedSignIn({ customProvider: groupId })
              .then(() => {
                sessionStorage.removeItem(StorageKey.FromLogin);
                navigate(redirectTo, { replace: true });
              })
              .catch(err => {
                setCognitoError(err);
              });
        } else {
          setSsoNotFound(true);
        }
      });
    },
    [getGroupId, navigate, qp, redirectTo]
  );

  return (
    <>
      {isLoading && <Skeleton active={isLoading} />}
      {!isLoading && (
        <Form form={form} initialValues={initialValues} onFinish={onFinish} layout={'vertical'} className={styles.form}>
          <div className={styles.heading}>
            <h3 className={styles.title}>
              <span>{t('Auth.sso.title')}</span>
            </h3>
            <span className={styles.desc}>{t('Auth.sso.desc')}</span>
          </div>
          <Form.Item
            name="email"
            label={t('Auth.emailAddress')}
            rules={[{ required: true, message: t('Auth.enterEmail') }]}
          >
            <Input name="email" placeholder={t('Common.email')} autoComplete={'username'} type={'email'} />
          </Form.Item>
          <div className={styles.actions}>
            <Button htmlType={'submit'} loading={isSubmitting} type={'primary'}>
              {t('Auth.common.buttons.continue')}
            </Button>
            <AuthBack />
          </div>
        </Form>
      )}
      {warningVisible && (
        <AuthWarning
          title={ssoNotFound ? t('Auth.sso.ssoNotFound') : undefined}
          description={
            error?.message === AmplifyError.DisabledUser
              ? t(`Auth.common.errors.${AmplifyError.DisabledUser}`)
              : !!awsError
              ? cognito_tr(awsError.errorMessage, { option: awsError.option })
              : ssoNotFound
              ? t('Auth.sso.errors.notEnabled')
              : ''
          }
          onClose={onCloseWarning}
        />
      )}
    </>
  );
};

export default SignInSSOPage;
